/**
 * syntheticSymbols.service — API client for the Synthetic Symbols feature
 * (docs/SYNTHETIC-SYMBOLS-PLAN.md). Server-gated by SYNTHETIC_SYMBOLS_ENABLED:
 * every call returns { ok:false, disabled:true } on 403 so the UI can hide
 * itself cleanly while the flag is off.
 */
import api from './api';

export interface SyntheticLeg {
  action: 'BUY' | 'SELL';
  optionType: 'CE' | 'PE';
  strike: number;
  expiry: string;
  symbol?: string | null;
  ratio: number;
}

export interface SyntheticSymbolRow {
  id: number;
  name: string;
  underlyingId: number;
  chipType: string;
  expiryBucket: 'D0' | 'D1' | 'D2' | 'W0' | 'M0';
  legs: SyntheticLeg[];
  isCredit: boolean;
  viewAligned: 'UP' | 'DOWN' | 'NEUT';
  source: 'AUTO' | 'MANUAL' | 'ENGINE' | 'SCAN';
  status: 'SAVED' | 'ARMED' | 'LIVE' | 'LIVE_PARTIAL' | 'CLOSED' | 'EXPIRED' | 'DELETED';
  basketId?: number | null;
  /** ENGINE mirrors only — the running OptionStrategyCycle this row reflects. */
  strategyCycleId?: number | null;
  nearestLegExpiryMs?: number | null;
  /** Scanner provenance (SCAN rows): engine, timeframe, signal, when. */
  scanInfo?: { engine: string; label: string; timeframeMin: number; candleType?: string; signal: string; at: string } | null;
  generatedSpot?: number | null;
  underlying?: { symbol: string; feedSource: string; zerodhaExchange?: string; lotSize?: number } | null;
  trigger?: SyntheticTriggerRow | null;
  /** Full scale-in group E1..EN (armed). */
  triggers?: SyntheticTriggerRow[];
  /** Per-lot economics + the underlying's OPTION budget (for auto-sizing the entries). */
  sizing?: {
    marginPerLot: number; netPremiumPerLot: number;
    optInvestmentAmountBuy: number; optInvestmentAmountSell: number; leverage: number;
  } | null;
  /** Basket-backed LIVE synthetic — the fired position's levels in CHART units +
   *  ₹-per-chart-point (Entry/SL/Target chart lines with money labels). */
  basketLive?: {
    basketId: number; side: 'LONG' | 'SHORT'; lots: number;
    entryPremium: number | null; slPremium: number | null; targetPremium: number | null;
    inrPerChartPoint: number; status?: string; autoTrailEnabled?: boolean;
  } | null;
  /** Per-tranche fill markers of the fired scale-in group (used to blend the Entry line). */
  firedEntries?: Array<{ basketId: number; entryIndex?: number | null; entryCount?: number | null; side: 'LONG' | 'SHORT'; entryPremium: number; placedPremium?: number; lots: number; inrPerChartPoint: number }>;
  /** ENGINE mirrors only — the running cycle's economics (read-only chart lines + readout). */
  engineCycle?: {
    engine: string; mode: 'PAPER' | 'LIVE'; status: 'OPEN' | 'CLOSED';
    entryAt?: string | null; exitDueAt?: string | null; entrySpot?: number | null;
    realizedPnl: number; entryPremium: number | null; stopPremium: number | null;
    trailSl?: number | null; structure?: string | null; tier?: string | null;
    legs: Array<{ legSymbol: string; side: 'BUY' | 'SELL'; optType: 'CE' | 'PE'; strike: number; expiry: string; qty: number; entryPremium: number | null; slPremium: number | null }>;
  } | null;
}

export interface SyntheticTriggerRow {
  id: number;
  side: 'LONG' | 'SHORT';
  groupId?: string | null;
  entryIndex?: number;
  entryCount?: number;
  entryPremium: number;
  slPremium?: number | null;
  targetPremium?: number | null;
  lots: number;
  triggerDirection: 'DIP' | 'STOP';
  tradingMode: 'PAPER' | 'LIVE';
  status: string;
}

type ListResult = { ok: boolean; disabled?: boolean; rows: SyntheticSymbolRow[] };

const disabledOn403 = (e: any) => e?.response?.status === 403;

export const syntheticSymbolsService = {
  async list(pane?: 'main' | 'live' | 'auto' | 'scan', underlying?: string): Promise<ListResult> {
    try {
      const r = await api.get<any>('/synthetic-symbols', {
        params: { ...(pane ? { pane } : {}), ...(underlying ? { underlying } : {}) },
        headers: { _suppressToast: 'true' },
      });
      return { ok: true, rows: r.data?.data || [] };
    } catch (e: any) {
      return { ok: false, disabled: disabledOn403(e), rows: [] };
    }
  },

  async getOne(id: number): Promise<SyntheticSymbolRow | null> {
    try {
      const r = await api.get<any>(`/synthetic-symbols/${id}`, { headers: { _suppressToast: 'true' } });
      return r.data?.data || null;
    } catch { return null; }
  },

  async create(body: { underlyingSymbol: string; strategyId?: string | null; legs: any[]; netPremium: number; viewAligned?: string }):
    Promise<{ ok: boolean; name?: string; id?: number; reason?: string }> {
    try {
      const r = await api.post<any>('/synthetic-symbols', body);
      return { ok: true, name: r.data?.data?.name, id: r.data?.data?.id };
    } catch (e: any) {
      return { ok: false, reason: e?.response?.data?.error?.message || e?.message };
    }
  },

  async refreshAuto(underlyingSymbol: string): Promise<{ ok: boolean; created?: number; reason?: string }> {
    try {
      const r = await api.post<any>('/synthetic-symbols/refresh-auto', { underlyingSymbol });
      return { ok: true, created: r.data?.data?.created };
    } catch (e: any) {
      return { ok: false, reason: e?.response?.data?.error?.message || e?.message };
    }
  },

  async remove(id: number): Promise<{ ok: boolean; reason?: string }> {
    try { await api.delete(`/synthetic-symbols/${id}`); return { ok: true }; }
    catch (e: any) { return { ok: false, reason: e?.response?.data?.error?.message || e?.message }; }
  },

  async quote(id: number): Promise<{ v: number; perLeg?: Array<{ symbol: string; mark: number }> } | null> {
    try {
      const r = await api.get<any>(`/synthetic-symbols/${id}/quote`, { headers: { _suppressToast: 'true' } });
      return r.data?.data || null;
    } catch { return null; }
  },

  /** Place the basket IMMEDIATELY at the live combined premium (no level-cross wait).
   *  SL/Target default server-side to ±50% of the live premium when omitted. */
  async tradeNow(id: number, body: { lots?: number; tradingMode?: 'PAPER' | 'LIVE'; slPremium?: number | null; targetPremium?: number | null }):
    Promise<{ ok: boolean; basketId?: number | null; entryPremium?: number; slPremium?: number | null; targetPremium?: number | null; reason?: string }> {
    try {
      const r = await api.post<any>(`/synthetic-symbols/${id}/trade-now`, body);
      return { ok: true, ...(r.data?.data || {}) };
    } catch (e: any) {
      return { ok: false, reason: e?.response?.data?.error?.message || e?.message };
    }
  },

  async armTrigger(id: number, body: {
    side: 'LONG' | 'SHORT'; entryPremium?: number; slPremium?: number | null; targetPremium?: number | null;
    lots?: number; tradingMode?: 'PAPER' | 'LIVE'; trailingEnabled?: boolean;
    /** Split-entry scale-in: E1..EN with their own lots (one shared SL/Target). */
    entries?: Array<{ entryPremium: number; lots: number }>;
  }):
    Promise<{ ok: boolean; direction?: string; reason?: string }> {
    try {
      const r = await api.post<any>(`/synthetic-symbols/${id}/trigger`, body);
      return { ok: true, direction: r.data?.data?.direction };
    } catch (e: any) {
      return { ok: false, reason: e?.response?.data?.error?.message || e?.message };
    }
  },

  async patchTrigger(id: number, body: { entryPremium?: number; slPremium?: number | null; targetPremium?: number | null; lots?: number; triggerId?: number }):
    Promise<{ ok: boolean; reason?: string }> {
    try { await api.put(`/synthetic-symbols/${id}/trigger`, body, { headers: { _suppressToast: 'true' } }); return { ok: true }; }
    catch (e: any) { return { ok: false, reason: e?.response?.data?.error?.message || e?.message }; }
  },

  /** Without `triggerId`: disarm the whole scale-in group. With it: remove ONE armed level. */
  async cancelTrigger(id: number, triggerId?: number): Promise<{ ok: boolean; reason?: string }> {
    try {
      await api.delete(`/synthetic-symbols/${id}/trigger`, { params: triggerId != null ? { triggerId } : {} });
      return { ok: true };
    }
    catch (e: any) { return { ok: false, reason: e?.response?.data?.error?.message || e?.message }; }
  },
};

/** Color for a synthetic-symbol row (plan §5 matrix): Up-aligned = green,
 *  Down-aligned = red; DEBIT darker, CREDIT lighter; neutral = amber-grey. */
export function syntheticColor(row: Pick<SyntheticSymbolRow, 'isCredit' | 'viewAligned'>): string {
  if (row.viewAligned === 'UP') return row.isCredit ? '#81c784' : '#2e7d32';   // credit lighter / debit darker green
  if (row.viewAligned === 'DOWN') return row.isCredit ? '#ef9a9a' : '#c62828'; // credit lighter / debit darker red
  return row.isCredit ? '#ffb74d' : '#8d6e63';                                  // neutral chips: amber (Cr) / brown (Db)
}
